import type { z } from 'zod'
import { isExpectedFormat } from '~/composables/isExpectedFormat'
import type { ApiFieldIssue } from '~/services/api/ApiFieldIssue'
import { PhoneNumber } from '~/utils/PhoneNumber'

const pointerOf = (path: readonly PropertyKey[]): string =>
  path.map((segment) => `/${String(segment).replace(/~/g, '~0').replace(/\//g, '~1')}`).join('')

const isCountable = (origin: string): boolean =>
  origin === 'string' || origin === 'array' || origin === 'set'

// A phone rule rides along as a param so the issue becomes the typed code that presents it.
const customFieldIssue = (params: unknown, source: ApiFieldIssue['source']): ApiFieldIssue => {
  const verdict = PhoneNumber.ruleVerdict(params)
  return verdict === undefined
    ? { code: 'FIELD_INVALID_FORMAT', params: { expected_format: 'pattern' }, source }
    : { ...PhoneNumber.fieldIssue(verdict), source }
}

const singleFieldIssue = (issue: z.core.$ZodIssue): ApiFieldIssue => {
  const source = { pointer: pointerOf(issue.path) }

  switch (issue.code) {
    case 'invalid_type':
      return {
        code:
          issue.input === undefined || issue.input === null
            ? 'FIELD_REQUIRED'
            : 'FIELD_INVALID_TYPE',
        source
      }
    case 'too_small':
      if (!isCountable(issue.origin)) {
        return { code: 'FIELD_OUT_OF_RANGE', params: { min: Number(issue.minimum) }, source }
      }
      return Number(issue.minimum) === 1
        ? { code: 'FIELD_REQUIRED', source }
        : { code: 'FIELD_TOO_SHORT', params: { min: Number(issue.minimum) }, source }
    case 'too_big':
      return isCountable(issue.origin)
        ? { code: 'FIELD_TOO_LONG', params: { max: Number(issue.maximum) }, source }
        : { code: 'FIELD_OUT_OF_RANGE', params: { max: Number(issue.maximum) }, source }
    case 'invalid_format':
      return {
        code: 'FIELD_INVALID_FORMAT',
        params: { expected_format: isExpectedFormat(issue.format) ? issue.format : 'pattern' },
        source
      }
    case 'not_multiple_of':
      return { code: 'FIELD_INVALID_FORMAT', params: { expected_format: 'pattern' }, source }
    case 'invalid_value':
      return {
        code: 'FIELD_INVALID_ENUM_VALUE',
        params: { allowed_values: issue.values.map(String) },
        source
      }
    case 'invalid_key':
    case 'invalid_element':
      return { code: 'FIELD_INVALID_TYPE', source }
    case 'custom':
      return customFieldIssue(issue.params, source)
    case 'unrecognized_keys':
      return { code: 'FIELD_NOT_ALLOWED', source }
    case 'invalid_union':
      return { code: 'FIELD_INVALID_FORMAT', params: { expected_format: 'pattern' }, source }
  }
}

export const zodIssueToFieldIssues = (issue: z.core.$ZodIssue): ApiFieldIssue[] =>
  issue.code === 'unrecognized_keys'
    ? issue.keys.map((key) => ({
        code: 'FIELD_NOT_ALLOWED',
        source: { pointer: pointerOf([...issue.path, key]) }
      }))
    : [singleFieldIssue(issue)]
